import { computed, Injectable, signal } from '@angular/core';

/**
 * Service centralisé pour gérer tous les états de loading des campagnes
 */
@Injectable({
  providedIn: 'root',
})
export class CampaignLoadingService {
  private readonly _measuresLoading = signal(false);
  private readonly _auscultationLoading = signal(false);
  private readonly _historyLoading = signal(false);
  private readonly _generalLoading = signal(false);
  private readonly _campaignDataLoading = signal(false);

  private readonly _initialLoadCompleted = signal(false);
  private readonly _measuresInitialLoadCompleted = signal(false);
  private readonly _auscultationInitialLoadCompleted = signal(false);
  private readonly _historyInitialLoadCompleted = signal(false);

  readonly measuresLoading = this._measuresLoading.asReadonly();
  readonly auscultationLoading = this._auscultationLoading.asReadonly();
  readonly historyLoading = this._historyLoading.asReadonly();
  readonly generalLoading = this._generalLoading.asReadonly();
  readonly campaignDataLoading = this._campaignDataLoading.asReadonly();

  readonly initialLoadCompleted = this._initialLoadCompleted.asReadonly();
  readonly measuresInitialLoadCompleted = this._measuresInitialLoadCompleted.asReadonly();
  readonly auscultationInitialLoadCompleted = this._auscultationInitialLoadCompleted.asReadonly();
  readonly historyInitialLoadCompleted = this._historyInitialLoadCompleted.asReadonly();

  readonly isLoading = computed(() => {
    return (
      this._measuresLoading() ||
      this._auscultationLoading() ||
      this._historyLoading() ||
      this._generalLoading() ||
      this._campaignDataLoading()
    );
  });

  readonly isMainDataLoading = computed(() => {
    return this._campaignDataLoading() && !this._initialLoadCompleted();
  });

  readonly canShowComponents = computed(() => {
    return this._initialLoadCompleted();
  });

  readonly canShowMeasuresTable = computed(() => {
    return this._measuresInitialLoadCompleted();
  });

  readonly canShowAuscultationData = computed(() => {
    return this._auscultationInitialLoadCompleted();
  });

  readonly canShowHistory = computed(() => {
    return this._historyInitialLoadCompleted();
  });

  setMeasuresLoading(loading: boolean): void {
    this._measuresLoading.set(loading);
    if (!loading) {
      this._measuresInitialLoadCompleted.set(true);
    }
  }

  setAuscultationLoading(loading: boolean): void {
    this._auscultationLoading.set(loading);
    if (!loading) {
      this._auscultationInitialLoadCompleted.set(true);
    }
  }

  setHistoryLoading(loading: boolean): void {
    this._historyLoading.set(loading);
    if (!loading) {
      this._historyInitialLoadCompleted.set(true);
    }
  }

  setGeneralLoading(loading: boolean): void {
    this._generalLoading.set(loading);
  }

  setCampaignDataLoading(loading: boolean): void {
    this._campaignDataLoading.set(loading);
    if (!loading) {
      this._initialLoadCompleted.set(true);
    }
  }

  // Re-arm on each asset load: a prior asset's "completed" latch must not suppress the new asset's skeleton.
  reset(): void {
    this._measuresLoading.set(false);
    this._auscultationLoading.set(false);
    this._historyLoading.set(false);
    this._generalLoading.set(false);
    this._campaignDataLoading.set(false);
    this._initialLoadCompleted.set(false);
    this._measuresInitialLoadCompleted.set(false);
    this._auscultationInitialLoadCompleted.set(false);
    this._historyInitialLoadCompleted.set(false);
  }

  async executeWithLoading<T>(
    operation: () => Promise<T>,
    loadingType: 'measures' | 'auscultation' | 'history' | 'general' | 'campaignData',
  ): Promise<T> {
    const setLoading = this.getLoadingSetter(loadingType);

    try {
      setLoading(true);
      return await operation();
    } finally {
      setLoading(false);
    }
  }

  private getLoadingSetter(type: string): (loading: boolean) => void {
    switch (type) {
      case 'measures':
        return loading => this.setMeasuresLoading(loading);
      case 'auscultation':
        return loading => this.setAuscultationLoading(loading);
      case 'history':
        return loading => this.setHistoryLoading(loading);
      case 'general':
        return loading => this.setGeneralLoading(loading);
      case 'campaignData':
        return loading => this.setCampaignDataLoading(loading);
      default:
        return () => {};
    }
  }
}
